import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAppMode } from "../../context/AppMode";
import { useUserHomeSelect } from "../../hooks/useUserHomeSelect";
import { Icons } from "../../utils/icons";
import UserCard from "../users/UserCard";
import ChangelogModal from "./ChangelogModal";
import UserSelectorModal from "./UserSelectorModal";

export default function Header() {
  const { mode, resetMode } = useAppMode();
  const { user, clearUser, selectUser } = useUserHomeSelect();
  const navigate = useNavigate();
  const [isUserModalOpen, setUserModalOpen] = useState(
    mode === "user" && !user,
  );
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);

  // Ouvre automatiquement la modale user si mode user et aucun user sélectionné
  useEffect(() => {
    if (mode === "user" && !user) {
      setUserModalOpen(true);
    }
  }, [mode, user]);

  // Ferme le menu mobile quand on change de page
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [navigate]);

  const handleLogout = () => {
    resetMode();
    clearUser();
    navigate("/select");
    setMobileMenuOpen(false);
  };

  const openUserCard = () => {
    if (user) setSelectedUserId(user.id);
    setMobileMenuOpen(false);
  };

  const closeUserCard = () => setSelectedUserId(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleOpenChangelog = () => {
    setShowChangelogModal(true);
    setMobileMenuOpen(false);
  };

  const handleCloseChangelog = () => {
    setShowChangelogModal(false);
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          {/* Mobile Header */}
          <div className="header-mobile">
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <Icons.X /> : <Icons.Menu />}
            </button>

            <h1 className="header-title-mobile">
              <Icons.AppIcon />
              <span>Calendrier associatif</span>
            </h1>

            {/* Mode badge mobile */}
            {mode !== "admin" && (
              <button
                className="mode-badge-mobile mode-user"
                onClick={openUserCard}
                title="Voir votre profil"
              >
                <Icons.User />
              </button>
            )}
          </div>

          {/* Desktop Header */}
          <div className="header-desktop">
            <h1 className="header-title">
              <Icons.AppIcon style={{ marginRight: "12px" }} />
              Calendrier associatif
            </h1>

            <nav className="header-nav">
              <NavLink className="nav-link" to="/calendar">
                <Icons.Calendar /> Calendrier
              </NavLink>
              {mode === "admin" && (
                <NavLink className="nav-link" to="/users">
                  <Icons.User /> Membres
                </NavLink>
              )}
            </nav>
            <div className="header-actions-group">
              <button
                className="btn btn-outline-secondary"
                onClick={handleLogout}
              >
                Changer de profil
              </button>
              {/* Label de mode desktop */}
              {mode === "admin" ? (
                <button className="mode-label" title="Administration">
                  <Icons.Settings className="icon-small" /> Admin
                </button>
              ) : (
                <button
                  className="mode-label"
                  onClick={openUserCard}
                  title="Voir votre profil"
                >
                  <Icons.User className="icon-small" />
                  {user ? user.name : "Utilisateur"}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={toggleMobileMenu}>
          <nav className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <h2>Menu</h2>
              <button onClick={toggleMobileMenu} aria-label="Fermer">
                <Icons.X />
              </button>
            </div>

            <div className="mobile-menu-content">
              {/* User info */}
              {mode === "user" && user && (
                <button className="mobile-user-card" onClick={openUserCard}>
                  <Icons.User className="mobile-user-icon" />
                  <div className="mobile-user-info">
                    <span className="mobile-user-name">{user.name}</span>
                    <span className="mobile-user-label">Voir mon profil</span>
                  </div>
                  <Icons.ChevronRight />
                </button>
              )}

              {/* Navigation links */}
              <div className="mobile-menu-links">
                {mode === "admin" && (
                  <NavLink
                    className="mobile-nav-link"
                    to="/users"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icons.User />
                    <span>Membres</span>
                  </NavLink>
                )}
              </div>

              {/* Mode indicator */}
              <div className="mobile-menu-footer">
                {/* Logout button */}
                <button
                  className="btn btn-outline-secondary"
                  onClick={handleLogout}
                >
                  <Icons.LogOut />
                  <span>Changer de profil</span>
                </button>
                {mode === "admin" ? (
                  <div className="btn btn-sm btn-danger">
                    <Icons.Settings /> Mode Admin
                  </div>
                ) : (
                  <div className="btn btn-sm btn-primary" aria-disabled>
                    <Icons.User /> Mode Utilisateur
                  </div>
                )}
              </div>
            </div>
          </nav>
        </div>
      )}

      <UserSelectorModal
        isOpen={mode === "user" && !user && isUserModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSelect={(selectedUser) => {
          selectUser(selectedUser);
          setUserModalOpen(false);
        }}
      />

      {/* UserCard Modal pour le user courant */}
      {selectedUserId && (
        <UserCard
          userId={selectedUserId}
          onClose={closeUserCard}
          onEdit={() => setSelectedUserId(null)}
          onDelete={() => setSelectedUserId(null)}
        />
      )}

      {/* Modale Changelog */}
      <ChangelogModal
        isOpen={showChangelogModal}
        onClose={handleCloseChangelog}
      />
    </>
  );
}
